import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { Modal, Notice } from '@/components/Modal';
import { PasswordConfirmModal } from '@/components/PasswordConfirmModal';
import { RelocationRouteMap } from '@/components/RelocationRouteMap';
import { searchAddress } from '@/lib/address/postcode';
import { queryKeys } from '@/lib/queryKeys';
import { safeHref } from '@/lib/utils/url';
import { markNoticeRead } from '@/pages/home/useHrProfile';
import { useCondos, useMyMallAccount, useRevealMallPassword } from '@/pages/mypage/useMyPageExtras';
import { ClaimRequestModal } from '@/pages/mypage/ClaimRequestModal';
import { useMyIncentive } from '@/pages/hr/useIncentive';
import { IncentivePanel } from '@/pages/mypage/IncentivePanel';
import { LodgingRequestModal } from '@/pages/mypage/LodgingRequestModal';
import { useExternalLinkUrls } from '@/lib/settings/useExternalLinks';
import {
  ASSET_CATEGORIES,
  useDeleteAsset,
  useMyAssets,
  useMyCareer,
  useMyRelocation,
  useMyWelfare,
  useRelocationPreview,
  useRelocationSites,
  useMyBirthday,
  useRequestClaim,
  useRequestHomecoming,
  useRequestLodging,
  useRevealMySoftwareKey,
  useSaveAsset,
  useSaveResidence,
  type AssetWrite,
  type BenefitOption,
  type CheckupTarget,
  type MyAsset,
  type MyRelocation,
  type MySoftwareSeat,
} from '@/pages/mypage/useWelfare';

/**
 * 복지관리 탭 (DOC-47 §3.3 · §3.4).
 *
 * ## 각 묶음의 첫 줄은 언제나 「내가 대상인가」다
 * 이력은 그 아래다 — 이 화면을 여는 이유가 그것이기 때문이다. 검진을 받았는지 기억나지 않아서
 * 열고, 교육을 들어야 하는지 몰라서 열고, 숙소를 신청할 수 있는지 몰라서 연다.
 *
 * ## 기준이 없으면 「기준 미등록」이라고 적는다
 * 검진 주기·경조금 표는 인사팀이 넣기 전까지 비어 있다. 그때 화면이 아무것도 말하지 않으면
 * 사람은 자기가 대상이 아니라고 읽는다. 비었다는 것과 대상이 아니라는 것은 다른 말이다.
 *
 * ## 숙소·귀향은 열 때만 계산한다
 * 라우팅(OSRM)을 부르는 유일한 자리라, 서브탭을 눌렀을 때 요청이 나간다.
 */

type SubTab =
  | 'benefit' | 'allowance' | 'lodging' | 'homecoming' | 'checkup' | 'asset' | 'mall'
  | 'incentive' | 'condo';

/**
 * 순서는 사용자가 정한다 (2026-09-22).
 *
 * <pre>
 *   1 경조·장학      2 수당        3 숙소       4 귀향여비    5 건강검진
 *   6 개인자산       7 생일·복지몰  8 성과급     9 콘도 예약
 * </pre>
 *
 * <p><b>교육이 빠졌다</b> — 마이페이지의 탭으로 나갔다({@code EducationTab}). 받는 것과 해야 하는
 * 것은 여는 이유가 다르다.
 *
 * <p><b>숙소와 귀향여비가 갈렸다.</b> 한 탭(「숙소·귀향여비」)이던 것을 둘로 나눈다 — 판정의
 * 근거(거주지·거리)는 같지만 <b>신청하는 일이 다르다</b>: 하나는 방을 받는 일이고 하나는 차비를
 * 정산받는 일이다. 근거 칸은 양쪽에 그대로 서고, 아래 신청 칸만 갈린다.
 *
 * <p>성과급·콘도는 <b>맨 뒤</b>다(사용자 결정 2026-09-22) — 앞의 일곱이 사용자가 부른 순서이고,
 * 이 둘은 이미 만들어 둔 것이라 그 뒤에 붙는다.
 */
const SUB_TABS: Array<{ key: SubTab; label: string }> = [
  { key: 'benefit', label: '경조·장학' },
  { key: 'allowance', label: '수당' },
  { key: 'lodging', label: '숙소' },
  { key: 'homecoming', label: '귀향여비' },
  { key: 'checkup', label: '건강검진' },
  { key: 'asset', label: '개인자산' },
  { key: 'mall', label: '생일·복지몰' },
  { key: 'incentive', label: '성과급' },
  { key: 'condo', label: '콘도 예약' },
];

/**
 * 성과급 탭은 <b>해당되는 사람에게만</b> 선다 (사용자 지시 2026-09-22: 「성과급탭은, 항목이 있는
 * 사람만 보이게 하자」).
 *
 * <p>대부분의 직원에게는 확정된 성과급도, 장으로 서 있는 건도 없다. 그런 사람에게 이 탭은 눌러야
 * 비어 있음을 알 수 있는 칸이고, 아홉 개짜리 탭 줄에서 자리만 먹는다.
 *
 * <p>판정은 <b>잠금 화면이 읽는 그 건수</b>로 한다({@code useMyIncentive} — 금액은 실리지 않는
 * 가벼운 응답이다). 금액을 보려면 여전히 비밀번호를 한 번 더 받는다: 탭이 보인다는 것과 열린다는
 * 것은 다른 이야기다.
 *
 * <p>건수를 아직 못 받았으면 <b>세우지 않는다</b>. 깜빡였다 사라지는 탭은 누르려던 손을 헛돌게 한다.
 */
function useVisibleSubTabs(): Array<{ key: SubTab; label: string }> {
  const gate = useMyIncentive();
  const hasIncentive = (gate.data?.awardCount ?? 0) > 0 || (gate.data?.leadRoundCount ?? 0) > 0;
  return SUB_TABS.filter((item) => item.key !== 'incentive' || hasIncentive);
}

export function WelfareTab() {
  /**
   * 서브탭도 <b>주소가 정할 수 있다</b>(`/mypage?tab=welfare&sub=lodging`).
   *
   * <p>홈 인사말이 「숙소·귀향여비 대상일 수 있어요」 라고 말해 놓고 건강검진 탭을 열면, 사람은
   * 방금 읽은 것을 여기서 다시 찾아야 한다 — 그 한 걸음에서 절반이 떨어져 나간다.
   * 모르는 값이면 조용히 기본 탭이다.
   */
  const [params] = useSearchParams();
  const [sub, setSub] = useState<SubTab>(
    () => SUB_TABS.find((item) => item.key === params.get('sub'))?.key ?? 'benefit',
  );

  // 복지 탭에 이미 들어와 있는 사람에게도 링크가 듣게 한다(MyPage 의 같은 이유).
  useEffect(() => {
    const next = SUB_TABS.find((item) => item.key === params.get('sub'))?.key;
    if (next) setSub(next);
  }, [params]);
  const { data, isPending, error } = useMyWelfare();
  const visibleTabs = useVisibleSubTabs();

  return (
    <div className="space-y-4">
      <div className="flex gap-1 flex-wrap" role="tablist">
        {visibleTabs.map((item) => (
          <button
            key={item.key}
            role="tab"
            aria-selected={sub === item.key}
            onClick={() => setSub(item.key)}
            className="rounded-lg"
            style={{
              fontSize: '13px',
              padding: '5px 12px',
              fontWeight: sub === item.key ? 700 : 500,
              background: sub === item.key ? 'var(--accent-bg)' : 'transparent',
              color: sub === item.key ? 'var(--accent)' : 'var(--text-secondary)',
              border: `1px solid ${sub === item.key ? 'var(--accent-light)' : 'var(--border)'}`,
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/*
        숙소와 귀향여비는 <b>같은 패널</b>이 갈래만 바꿔 그린다 — 거주지·거리 판정이 둘의 공통
        근거라, 두 벌로 만들면 한쪽만 고쳐지는 날이 온다.
      */}
      {sub === 'lodging' ? (
        <RelocationPanel mode="lodging" />
      ) : sub === 'homecoming' ? (
        <RelocationPanel mode="homecoming" />
      ) : sub === 'allowance' ? (
        <AllowancePanel />
      ) : sub === 'incentive' ? (
        <IncentivePanel />
      ) : sub === 'condo' ? (
        <CondoPanel />
      ) : sub === 'asset' ? (
        <AssetPanel />
      ) : sub === 'mall' ? (
        <MallPanel />
      ) : isPending ? (
        <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
      ) : error ? (
        <Notice tone="error">복지 자료를 불러오지 못했습니다.</Notice>
      ) : (
        <>
          {sub === 'checkup' && <CheckupPanel rows={data?.checkups ?? []} />}
          {sub === 'benefit' && (
            <BenefitPanel options={data?.benefitOptions ?? []} claims={data?.claims ?? []} />
          )}
        </>
      )}
    </div>
  );
}

// ── 건강검진 ─────────────────────────────────────────────────────────────────

function CheckupPanel({ rows }: { rows: CheckupTarget[] }) {
  const navigate = useNavigate();
  if (rows.length === 0) return <StandardMissing what="건강검진 기준" />;

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.standardId} className="rounded-lg" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: '13px', fontWeight: 700 }}>{row.name}</span>
            {row.overdue ? (
              <span className="badge badge-yellow">수검 대상</span>
            ) : (
              <span className="badge badge-green">기한 내</span>
            )}
            {row.checkupType === 'COMPREHENSIVE' && (
              <button
                className="btn-secondary ml-auto"
                style={compactButton}
                onClick={() => navigate('/approval/draft?formKey=CHECKUP_APPLY')}
              >
                종합검진 신청
              </button>
            )}
          </div>
          <p style={{ ...mutedText, marginTop: '4px' }}>
            {row.cycleMonths ? `${row.cycleMonths}개월 주기` : (row.reason ?? '주기 미등록')}
            {' · '}
            {row.lastDate ? `최근 수검 ${row.lastDate}` : '수검 기록 없음'}
            {row.dueOn && ` · 다음 기한 ${row.dueOn}`}
          </p>
        </div>
      ))}
      {/* 결과값을 담지 않는다는 사실을 사람에게도 알린다 — 회사가 무엇을 아는지가 분명해야 한다. */}
      <p style={mutedText}>{/* 검진 결과(수치·소견)는 회사에 저장되지 않습니다. 수검 여부만 기록됩니다. */}</p>
    </div>
  );
}

// ── 교육은 마이페이지의 탭이 됐다 ───────────────────────────────────────────
//
// 법정의무교육·기술인 교육 두 칸은 `EducationTab.tsx` 로 옮겼다(사용자 지시 2026-09-22).
// 복지의 다른 것들은 「내가 대상인가·얼마 받나」 이고 교육은 「내가 해야 할 일」 이라, 같은 줄에
// 두면 뒤쪽이 묻힌다. 응답(`useMyWelfare`)은 그대로라 그쪽에서 같은 훅을 읽는다.

// ── 콘도 ─────────────────────────────────────────────────────────────────────

/**
 * 콘도 예약 (V321 · 사용자 지시 2026-09-22 · 「안내 + 신청 결재 연동」).
 *
 * <h2>예약 달력을 두지 않는다</h2>
 * 자리는 총무가 협의해 배정한다. 화면이 빈자리를 말하면 「달력에는 비었는데 실제로는 찼다」 가
 * 생기고, 그 한 번으로 아무도 이 화면을 믿지 않게 된다. 여기서 답하는 것은 <b>무엇이 있고 어떻게
 * 쓰는가</b>이고, 신청은 결재가 받는다.
 *
 * <h2>등록된 것이 없으면 그렇게 적는다</h2>
 * 인사팀이 넣기 전까지 비어 있다. 비었다는 것과 콘도가 없다는 것은 다른 말이라, 「기준 미등록」
 * 과 같은 투로 말한다 — 대신 신청 버튼은 그대로 선다(막힌 이유는 눌렀을 때 서버가 답한다).
 */
function CondoPanel() {
  const navigate = useNavigate();
  const { data, isPending, error } = useCondos();

  if (isPending) return <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>;
  if (error) return <Notice tone="error">콘도 자료를 불러오지 못했습니다.</Notice>;

  const rows = data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <p style={{ ...sectionTitle, marginBottom: 0 }}>회사 콘도</p>
        <button
          className="btn-secondary ml-auto"
          style={compactButton}
          onClick={() => navigate('/approval/draft?formKey=CONDO_APPLY')}
        >
          콘도 신청
        </button>
      </div>

      {rows.length === 0 ? (
        <StandardMissing what="콘도 정보" />
      ) : (
        rows.map((condo) => (
          <div key={condo.id} className="rounded-lg" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
            <div className="flex items-center gap-2 flex-wrap">
              <span style={{ fontSize: '13px', fontWeight: 700 }}>{condo.name}</span>
              {condo.location && <span style={mutedText}>{condo.location}</span>}
              {condo.contact && <span style={mutedText}>· {condo.contact}</span>}
              {condo.linkUrl && (
                <a
                  href={safeHref(condo.linkUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary ml-auto"
                  style={compactButton}
                >
                  예약처 열기
                </a>
              )}
            </div>
            {/* 이용안내는 인사팀이 적은 줄바꿈 그대로 보인다 — 성수기·요금·취소가 줄마다 다르다. */}
            {condo.guide && (
              <p style={{ ...mutedText, marginTop: '6px', whiteSpace: 'pre-wrap' }}>{condo.guide}</p>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ── 경조 · 장학 ──────────────────────────────────────────────────────────────

function BenefitPanel({ options, claims }: { options: BenefitOption[]; claims: MyBenefitClaim[] }) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div>
        <p style={sectionTitle}>신청할 수 있는 항목</p>
        {options.length === 0 ? (
          <StandardMissing what="경조금·장학금 기준" />
        ) : (
          <div className="space-y-1">
            {options.map((option) => (
              <div
                key={option.standardId}
                className="flex items-center gap-3"
                style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}
              >
                <span className="badge badge-gray">{option.kind === 'CONGRATULATION' ? '경조' : '장학'}</span>
                <span style={{ fontWeight: 600 }}>{option.subject ?? option.name}</span>
                {option.condition && <span style={mutedText}>{option.condition}</span>}
                {/*
                  금액을 여기서 보여 주는 이유: 기안 화면이 이 값을 그대로 채운다. 사람이 지침을
                  뒤져 옮겨 적으면 거기서 틀린다(경조금은 근속 구간마다 금액이 다르다 — 서버가
                  <b>내 구간의 줄</b>을 골라 내려보낸다).
                */}
                {option.amount != null && <span>{option.amount.toLocaleString('ko-KR')}원</span>}
                {/*
                  장학금은 금액이 아니라 <b>비율</b>이다. 「50%」 만 적어 두면 무엇의 50%인지 알 수
                  없으므로 무엇에 곱하는지까지 말한다 — 그 값은 사람이 기안에 적는다.
                */}
                {option.amount == null && option.rate != null && (
                  <span>
                    {option.rate === 0
                      ? '지급 대상 아님'
                      : `신청액의 ${option.rate}%`}
                  </span>
                )}
                {option.leaveDays != null && option.leaveDays > 0 && (
                  <span className="badge badge-blue">경조휴가 {option.leaveDays}일</span>
                )}
                <span className="ml-auto">
                  {option.eligible ? (
                    <button
                      className="btn-secondary"
                      style={compactButton}
                      onClick={() => navigate(`/approval/draft?formKey=${option.kind}`)}
                    >
                      신청
                    </button>
                  ) : (
                    <span style={mutedText}>{option.reason}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p style={sectionTitle}>신청 이력</p>
        {claims.length === 0 ? (
          <p style={{ ...mutedText, padding: '8px 0' }}>신청한 내역이 없습니다.</p>
        ) : (
          claims.map((claim) => (
            <div
              key={claim.id}
              className="flex items-center gap-3"
              style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}
            >
              <span className="badge badge-gray">{claim.claimType === 'CONGRATULATION' ? '경조' : '장학'}</span>
              <span>{claim.subject ?? '-'}</span>
              <span style={mutedText}>{claim.eventDate ?? ''}</span>
              <span className="ml-auto" style={{ fontWeight: 700 }}>
                {claim.amount != null ? `${claim.amount.toLocaleString('ko-KR')}원` : '-'}
              </span>
              <span className="badge badge-blue">{claim.status}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

type MyBenefitClaim = {
  id: string;
  claimType: string;
  subject: string | null;
  eventDate: string | null;
  amount: number | null;
  leaveDays: number | null;
  status: string;
};

// ── 수당 ─────────────────────────────────────────────────────────────────────

/**
 * 수당 대상 (사용자 지시 2026-08-27).
 *
 * ## 왜 여기에도 두는가
 * 판정 자체는 「인사」 탭에 이미 있었다. 그런데 사람들은 수당을 <b>복지로 여겨</b> 이 탭에서 찾고,
 * 없으면 「화면이 없다」 고 읽는다. 있는 것을 못 찾는 것과 없는 것은 사용자에게 같은 일이다.
 *
 * ## 인사 탭에서는 뺐다
 * 두 곳에 같은 표를 두면 문구가 갈린다 — 특히 <b>「지급 금액과 반영 시점은 인사·급여 담당이
 * 확정합니다」</b> 같은 줄은 한쪽만 고쳐지는 순간 두 화면이 다른 말을 한다. 그래서 <b>여기 한 곳</b>
 * 이고, 인사 탭은 그 판정의 <b>근거</b>(직책·선임·자격증)를 담는 자리로 남는다.
 *
 * ## 이 서브탭을 눌러야 부른다
 * 자료는 인사 응답(`/mypage/career`)에 실려 온다. 이 컴포넌트는 서브탭을 눌렀을 때 처음 붙으므로,
 * 복지 탭을 열기만 한 사람은 그 요청을 내지 않는다.
 */
function AllowancePanel() {
  const { data, isPending, error } = useMyCareer();
  const queryClient = useQueryClient();

  /*
    이 탭을 연 것이 곧 「확인했다」 다 — 급여·고과와 달리 재인증이 없는 화면이라, 여기 도달한
    것 말고는 확인의 증거가 없다. 홈 위젯의 수당 줄은 이것으로 접히고, <b>새 현장으로 발령이
    나면 다시</b> 선다(기준이 배치 시작일이다).
  */
  useEffect(() => {
    markNoticeRead('ALLOWANCE');
    void queryClient.invalidateQueries({ queryKey: queryKeys.homeProfile });
  }, [queryClient]);

  if (isPending) return <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>;
  if (error) return <Notice tone="error">수당 자료를 불러오지 못했습니다.</Notice>;

  const allowances = data?.allowances ?? [];

  /*
    <b>갈래로 묶어 적는다</b> (사용자 지시 2026-09-22: 「수당 - 표기. 정리 (항목) 자격 근속 직책
    현장 겸직에 대한내용 포함되어서」).

    서버는 이미 다섯 갈래로 판정한다({@code AllowanceStandard.kind} — LICENSE·TENURE·DUTY·SITE·
    CONCURRENT). 그런데 화면은 그것을 <b>한 줄로 평평하게</b> 늘어놓고 있어서, 줄이 열을 넘으면
    「무엇 때문에 대상인가」 가 사라졌다 — 갈래는 그 줄의 이름 안에만 있었다.

    갈래가 곧 <b>기준의 축</b>이다. 묶어 두면 「자격 수당은 둘 다 대상인데 직책 수당은 아니다」 가
    한눈에 읽히고, 갈래마다 붙는 「대상 n / 전체 m」 이 그 축의 요약이 된다.
  */
  const groups = ALLOWANCE_KINDS
    .map((kind) => ({ ...kind, rows: allowances.filter((row) => row.kind === kind.key) }))
    .filter((group) => group.rows.length > 0);
  // 다섯에 들지 않는 갈래가 생기면 그것도 보여 준다 — 조용히 사라지면 아무도 모른다.
  const others = allowances.filter((row) => !ALLOWANCE_KINDS.some((kind) => kind.key === row.kind));
  const shown = others.length > 0
    ? [...groups, { key: 'ETC', label: '그 밖', hint: null, rows: others }]
    : groups;

  return (
    <div className="space-y-3">
      {allowances.length === 0 ? (
        <Notice tone="info">
          수당 기준이 아직 등록되지 않았습니다. 인사팀이 사내 지침을 등록하면 대상 여부가 표시됩니다.
        </Notice>
      ) : (
        <>
          {/* 근속수당 알림(사용자 지시 2026-10-28) — 근속 조건을 채운 수당. 신청을 받아야 하는 줄이다. */}
          {allowances.some((row) => row.eligible && row.tenureReachedOn) && (
            <Notice tone="info">
              <b>근속수당 알림</b>
              {allowances.filter((row) => row.eligible && row.tenureReachedOn).map((row) => (
                <div key={`tenure-${row.name}`}>{row.name} · {row.tenureReachedOn}</div>
              ))}
            </Notice>
          )}
          {shown.map((group) => (
            <div key={group.key} style={{ paddingTop: '4px' }}>
              <div className="flex items-baseline gap-2">
                <p style={{ ...sectionTitle, marginBottom: 0 }}>{group.label}</p>
                {group.hint && <span style={mutedText}>{group.hint}</span>}
                <span className="ml-auto" style={mutedText}>
                  대상 {group.rows.filter((row) => row.eligible).length} / {group.rows.length}
                </span>
              </div>
              {group.rows.map((allowance) => (
            <div key={`${allowance.kind}-${allowance.name}`} className="flex items-center gap-3" style={rowStyle}>
              <span style={{ fontWeight: 600, minWidth: '120px' }}>{allowance.name}</span>
              <span style={mutedText}>{allowance.reason ?? ''}</span>
              <span className="ml-auto flex items-center gap-1.5">
                {allowance.monthlyAmount != null && (
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {allowance.payCycle === 'YEAR_END' ? '연말 1회' : '월'} {allowance.monthlyAmount.toLocaleString()}원
                  </span>
                )}
                {/* 발령일 기준 일할(2026-10-28) — 이번 달이 다 차지 않았을 때만 그 달 금액을 함께 적는다. */}
                {allowance.monthAmount != null && allowance.paidDays != null && allowance.monthDays != null
                  && allowance.paidDays < allowance.monthDays && (
                  <span style={{ fontSize: '12px', color: 'var(--accent)' }}>
                    이번 달 {allowance.monthAmount.toLocaleString()}원 ({allowance.paidDays}/{allowance.monthDays}일)
                  </span>
                )}
                {/*
                  「확인 필요」가 <b>대상/미대상 앞에</b> 선다. 뒤에 두면 배지 두 개 중 큰 쪽만
                  읽히고, 정작 「이 판정을 믿어도 되는가」 라는 앞선 물음이 뒤로 밀린다.
                */}
                {!allowance.confirmed && (
                  <span
                    className="badge"
                    title="자격증 이름·근속 개월 수 같은 기준값이 아직 회사 지침으로 확정되지 않았습니다. 인사팀이 복지관리 › 기준에서 확정합니다."
                    style={{ background: '#fef3c7', color: '#92400e' }}
                  >
                    확인 필요
                  </span>
                )}
                {allowance.eligible ? (
                  <span className="badge badge-green">대상</span>
                ) : (
                  <span className="badge badge-gray">미대상</span>
                )}
              </span>
            </div>
                ))}
              </div>
            ))}
          {/* 예시 기준이 하나라도 섞여 있으면 표 아래에서 한 번 더 말한다 — 배지는 작아서 놓친다. */}
          {allowances.some((allowance) => !allowance.confirmed) && (
            <Notice tone="warning">
              <b>「확인 필요」</b>가 붙은 줄은 자격증 이름·근속 개월 수 같은 기준값이 아직 회사 지침으로
              확정되지 않았습니다. 그 줄의 대상 여부는 <b>참고값</b>입니다 — 인사팀이 복지관리 › 기준에서
              회사 수당표대로 고치면 표시가 사라집니다.
            </Notice>
          )}
          {/*
            금액은 기준표의 월 정액(V567)만 보인다. 통상임금에 걸리는 수당은 계산하지 않으니
            이 한 줄이 그 문의를 대신 받아 준다.
          */}
          <p style={{ ...mutedText, marginTop: '6px' }}>
            지급 금액과 반영 시점은 인사·급여 담당이 확정합니다.
          </p>
        </>
      )}
      {/*
        근거는 인사 탭에 있다. 「왜 대상이 아닌가」 는 직책·선임·자격증이 답하는 질문이라,
        그 셋이 함께 있는 화면으로 보낸다 — 여기서 다시 설명하면 근거가 두 곳으로 갈린다.
      */}
      <p style={mutedText}>
        직책·법정 선임·자격증 같은 <b>판정 근거</b>는 마이페이지 「인사」 탭에서 함께 볼 수 있습니다.
      </p>
    </div>
  );
}

// ── 개인자산 ─────────────────────────────────────────────────────────────────

/**
 * 개인자산 (사용자 지시 2026-09-03: 「개인자산 지급일, 정보 기입 (PC, 캐드, 한글, 마이크로소프트 등)」).
 *
 * ## 두 목록이 나란히 선다
 * 위는 <b>지급받은 자산</b>(PC·모니터·휴대폰)이고, 아래는 <b>총무의 배포 대장</b>이 아는 소프트웨어다
 * (캐드·한글·MS — V74). 합치지 않은 이유가 그것이다: 섞어 두면 캐드 줄에서도 연필을 찾게 되고,
 * 없으면 고장난 화면으로 읽힌다.
 *
 * ## 소프트웨어는 <b>불출되면 저절로</b> 올라온다
 * 신청 탭에서 담당자가 불출하면(V265) 배포 행에 내 `personnel_id` 가 박히고, 아래 목록은 그
 * 대장을 읽는다 — 본인이 다시 적는 자리가 아니다. 회수되면 같은 이유로 사라진다.
 *
 * ## 위 목록에도 <b>내가 못 고치는 줄</b>이 있다
 * 총무가 등록한 줄이다(V226 · 「인사총무팀 등록」). 회사가 지급한 사실을 받는 쪽이 고쳐 버리면
 * 그 줄은 더 이상 대장이 아니라서, 그 자리에는 손잡이 대신 <b>어디로 가면 되는지</b>가 선다.
 * 내가 적은 줄은 그대로 고치고 지운다 — 총무가 다 넣어 줄 때까지 기다리지 않아도 된다.
 *
 * ## 반납한 것은 지우지 않는다
 * 서버가 아래로 내려 준다. 「그때 무엇을 쓰고 있었나」 는 사고·감사에서 실제로 묻는 질문이다.
 */
export function AssetPanel() {
  const { data, isPending, error } = useMyAssets();
  const save = useSaveAsset();
  const remove = useDeleteAsset();
  /** 열려 있는 입력칸. `'new'` 면 새 줄, 자산 id 면 그 줄을 고치는 중이다. */
  const [editing, setEditing] = useState<string | null>(null);
  /** 한 번 더 눌러야 지운다 — 되돌릴 수 없는 방향이라 그 사이에 한 걸음을 둔다. */
  const [confirming, setConfirming] = useState<string | null>(null);
  /** 키를 열려는 라이선스 · 열린 키(V266). 열린 값은 캐시에 두지 않고 이 자리에서만 산다. */
  const [keySeat, setKeySeat] = useState<MySoftwareSeat | null>(null);
  const [revealedKey, setRevealedKey] = useState<{
    productName: string;
    licenseKey: string;
    /** 시리얼도 같이 온다 — 둘을 넣어야 설치된다(사용자 확인 2026-09-09). */
    serialNo: string | null;
  } | null>(null);
  const revealKey = useRevealMySoftwareKey();

  if (isPending) return <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>;
  if (error) return <Notice tone="error">자산 자료를 불러오지 못했습니다.</Notice>;

  const assets = data?.assets ?? [];
  const software = data?.software ?? [];

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2" style={{ marginBottom: '4px' }}>
          <p style={{ ...sectionTitle, marginBottom: 0 }}>지급받은 자산</p>
          <button
            className="btn-secondary ml-auto"
            style={compactButton}
            onClick={() => {
              save.reset();
              setEditing(editing === 'new' ? null : 'new');
            }}
          >
            {editing === 'new' ? '닫기' : '자산 추가'}
          </button>
        </div>

        {editing === 'new' && (
          <AssetForm
            pending={save.isPending}
            error={save.error?.message ?? null}
            onCancel={() => setEditing(null)}
            onSubmit={(body) => save.mutate(body, { onSuccess: () => setEditing(null) })}
          />
        )}

        {assets.length === 0 && editing !== 'new' ? (
          <p style={{ ...mutedText, padding: '8px 0' }}>등록한 자산이 없습니다.</p>
        ) : (
          assets.map((asset) => (
            editing === asset.id ? (
              <AssetForm
                key={asset.id}
                asset={asset}
                pending={save.isPending}
                error={save.error?.message ?? null}
                onCancel={() => setEditing(null)}
                onSubmit={(body) => save.mutate({ ...body, id: asset.id }, { onSuccess: () => setEditing(null) })}
              />
            ) : (
              <div key={asset.id} className="flex items-center gap-3 flex-wrap" style={rowStyle}>
                <span className="badge badge-gray">{categoryLabel(asset.category)}</span>
                {/*
                  총무가 등록한 줄에는 표시가 붙는다 — 손잡이가 없는 이유가 화면에 있어야 한다.
                  본인이 적은 줄에는 아무 표시도 하지 않는다: 대부분이 그쪽이고, 전부에 붙은 배지는
                  읽히지 않는다.
                */}
                {asset.source === 'HR' && <span className="badge badge-blue">인사총무팀 등록</span>}
                <span style={{ fontWeight: 600 }}>{asset.name}</span>
                {asset.spec && <span style={mutedText}>{asset.spec}</span>}
                {asset.serialNo && <span style={mutedText}>S/N {asset.serialNo}</span>}
                <span style={mutedText}>{asset.issuedOn ? `지급 ${asset.issuedOn}` : '지급일 미기입'}</span>
                {asset.returnedOn && <span className="badge badge-gray">{asset.returnedOn} 반납</span>}
                {asset.note && <span style={mutedText}>{asset.note}</span>}
                <span className="ml-auto flex items-center gap-1.5">
                  {asset.source === 'HR' ? (
                    <span style={mutedText}>인사팀 문의</span>
                  ) : (
                  <button
                    className="btn-secondary"
                    style={compactButton}
                    onClick={() => {
                      save.reset();
                      setConfirming(null);
                      setEditing(asset.id);
                    }}
                  >
                    수정
                  </button>
                  )}
                  {asset.source === 'HR' ? null : confirming === asset.id ? (
                    <button
                      className="btn-secondary"
                      style={{ ...compactButton, color: 'var(--danger)' }}
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(asset.id, { onSuccess: () => setConfirming(null) })}
                    >
                      정말 삭제
                    </button>
                  ) : (
                    <button className="btn-secondary" style={compactButton} onClick={() => setConfirming(asset.id)}>
                      삭제
                    </button>
                  )}
                </span>
              </div>
            )
          ))
        )}
        {remove.error && <p style={{ ...mutedText, color: 'var(--danger)' }}>{remove.error.message}</p>}
      </div>

      <div>
        <p style={sectionTitle}>소프트웨어</p>
        {software.length === 0 ? (
          <p style={{ ...mutedText, padding: '8px 0' }}>배포된 라이센스가 없습니다.</p>
        ) : (
          software.map((seat) => (
            <div key={seat.seatId} className="flex items-center gap-3 flex-wrap" style={rowStyle}>
              <span style={{ fontWeight: 600 }}>{seat.productName}</span>
              {seat.edition && <span className="badge badge-gray">{seat.edition}</span>}
              {seat.vendorName && <span style={mutedText}>{seat.vendorName}</span>}
              {/* 현장 몫이면 내 것이 아니라 «그 현장 것»이다. 그 사실이 줄에 보여야 한다. */}
              {seat.deviceName && <span style={mutedText}>{seat.deviceName}</span>}
              {seat.assignedOn && <span style={mutedText}>배포 {seat.assignedOn}</span>}
              <span className="ml-auto flex items-center gap-1.5">
                {seat.expiresOn && <span style={mutedText}>만료 {seat.expiresOn}</span>}
                {seat.expired && <span className="badge badge-yellow">만료</span>}
                {/*
                  키가 아직 안 들어온 줄에서도 <b>버튼은 선다</b>. 감추면 「내 캐드는 왜 키가 없지」 를
                  화면에 물을 자리가 없어진다 — 눌렀을 때 서버가 이유를 말한다(`SOFTWARE_KEY_EMPTY`).
                */}
                <button
                  className="btn-secondary"
                  style={compactButton}
                  onClick={() => {
                    revealKey.reset();
                    setKeySeat(seat);
                  }}
                >
                  키 보기
                </button>
              </span>
            </div>
          ))
        )}
      </div>

      {keySeat && (
        <PasswordConfirmModal
          title={`${keySeat.productName} 라이센스 키`}
          message="본인 비밀번호를 넣으면 키가 보입니다. 연 기록이 남습니다."
          confirmLabel="키 보기"
          isPending={revealKey.isPending}
          error={revealKey.error?.message ?? null}
          onClose={() => {
            setKeySeat(null);
            revealKey.reset();
          }}
          onConfirm={(password) =>
            revealKey.mutate(
              { seatId: keySeat.seatId, password },
              {
                onSuccess: (data) => {
                  setRevealedKey({
                    productName: data.productName,
                    licenseKey: data.licenseKey,
                    serialNo: data.serialNo,
                  });
                  setKeySeat(null);
                  revealKey.reset();
                },
              },
            )
          }
        />
      )}

      {revealedKey && (
        <Modal title={`${revealedKey.productName} 라이센스 키`} onClose={() => setRevealedKey(null)} maxWidth="420px">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: '12px',
              rowGap: '6px',
              alignItems: 'baseline',
              fontSize: '13px',
            }}
          >
            <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>키</span>
            <span style={{ fontFamily: 'monospace', fontSize: '15px', wordBreak: 'break-all', userSelect: 'all' }}>
              {revealedKey.licenseKey}
            </span>
            {/* 시리얼이 없는 제품도 있다 — 없으면 줄이 서지 않는다. */}
            {revealedKey.serialNo && (
              <>
                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>시리얼번호</span>
                <span style={{ fontFamily: 'monospace', fontSize: '15px', wordBreak: 'break-all', userSelect: 'all' }}>
                  {revealedKey.serialNo}
                </span>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * 자산 한 줄을 적는 칸. 새로 넣을 때와 고칠 때가 <b>같은 칸</b>이다 — 둘을 나누면 같은 자리에서
 * 두 벌의 유효성 규칙이 갈린다.
 */
function AssetForm({
  asset,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  asset?: MyAsset;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: AssetWrite) => void;
}) {
  const [category, setCategory] = useState(asset?.category ?? 'PC');
  const [name, setName] = useState(asset?.name ?? '');
  const [spec, setSpec] = useState(asset?.spec ?? '');
  const [serialNo, setSerialNo] = useState(asset?.serialNo ?? '');
  const [issuedOn, setIssuedOn] = useState(asset?.issuedOn ?? '');
  const [returnedOn, setReturnedOn] = useState(asset?.returnedOn ?? '');
  const [note, setNote] = useState(asset?.note ?? '');

  return (
    <div className="rounded-lg space-y-2" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
      <div className="flex items-end gap-2 flex-wrap">
        <select
          className="input-field"
          aria-label="구분"
          style={{ ...fieldStyle, maxWidth: '140px' }}
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        >
          {ASSET_CATEGORIES.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </select>
        <input
          className="input-field"
          aria-label="자산 이름"
          placeholder="삼성 노트북 NT750"
          style={{ ...fieldStyle, maxWidth: '200px' }}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <input
          className="input-field"
          aria-label="사양"
          placeholder="i7 / 32GB / 1TB"
          style={{ ...fieldStyle, maxWidth: '200px' }}
          value={spec}
          onChange={(event) => setSpec(event.target.value)}
        />
        <input
          className="input-field"
          aria-label="일련번호"
          placeholder="S/N"
          style={{ ...fieldStyle, maxWidth: '160px' }}
          value={serialNo}
          onChange={(event) => setSerialNo(event.target.value)}
        />
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <label style={mutedText}>
          지급일{' '}
          <input
            className="input-field"
            type="date"
            style={fieldStyle}
            value={issuedOn}
            onChange={(event) => setIssuedOn(event.target.value)}
          />
        </label>
        <label style={mutedText}>
          반납일{' '}
          <input
            className="input-field"
            type="date"
            style={fieldStyle}
            value={returnedOn}
            onChange={(event) => setReturnedOn(event.target.value)}
          />
        </label>
        <input
          className="input-field"
          aria-label="비고"
          placeholder="비고"
          style={{ ...fieldStyle, maxWidth: '220px' }}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <button
          className="btn-secondary"
          style={compactButton}
          disabled={pending || name.trim().length === 0}
          onClick={() => onSubmit({
            category,
            name: name.trim(),
            spec: spec.trim() || null,
            serialNo: serialNo.trim() || null,
            issuedOn: issuedOn || null,
            returnedOn: returnedOn || null,
            note: note.trim() || null,
          })}
        >
          {pending ? '저장 중…' : '저장'}
        </button>
        <button className="btn-secondary" style={compactButton} onClick={onCancel}>취소</button>
      </div>
      {error && <p style={{ ...mutedText, color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
}

function categoryLabel(value: string): string {
  return ASSET_CATEGORIES.find((item) => item.value === value)?.label ?? value;
}

// ── 생일 · 복지몰 ────────────────────────────────────────────────────────────

/**
 * 생일 · 복지몰 (사용자 지시 2026-09-03 · 계정 2026-09-22).
 *
 * ## 포인트는 여전히 없다
 * 잔액은 복지몰이 갖고 있고 우리는 받아 올 길이 없다(「복지몰은 그냥 링크같은거만 넣자.
 * 포인트를 연동할 순 없을 것 같은데」). 손으로 옮겨 적은 잔액은 그 순간부터 저쪽의 진짜 값과
 * 갈라지고, 화면은 그 사실을 모른 채 틀린 숫자를 자신 있게 보여 준다.
 *
 * ## 계정은 인사팀이 넣는다 (V321)
 * 「아이디 비밀번호 및 사용안내를 넣을 수 있도록」(2026-09-22). <b>사람마다 다른 계정</b>이라
 * 인사팀이 엑셀로 넣고, 여기서는 본인 것만 보인다.
 *
 * ## 비밀번호는 눌러야 보인다
 * 목록·응답에 담기지 않는다. 「보기」 를 누르고 <b>본인 비밀번호</b>를 한 번 더 넣으면 그 자리에서
 * 받아 보여 준다(라이선스 키와 같은 선) — 자리를 비운 화면에서 남이 읽지 못하게.
 *
 * ## 주소가 없으면 바로가기를 세우지 않는다
 * 복지몰 주소는 「환경설정 › 외부 API 설정 › 외부 링크」 가 정한다({@code ExternalLink.WELFARE_MALL}).
 * 비어 있는 동안 버튼을 세우면 눌러서 <b>빈 화면</b>으로 간다.
 */
function MallPanel() {
  const { data, isPending, error } = useMyBirthday();
  const account = useMyMallAccount();
  const reveal = useRevealMallPassword();
  const links = useExternalLinkUrls();
  const mallUrl = links.welfare_mall?.trim() ?? '';

  /** 물어보는 창이 열렸는가. 받은 비밀번호는 <b>이 자리에서만</b> 산다 — 캐시에 담지 않는다. */
  const [asking, setAsking] = useState(false);
  const [shown, setShown] = useState<string | null>(null);

  if (isPending) return <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>;
  if (error) return <Notice tone="error">생일 자료를 불러오지 못했습니다.</Notice>;

  const mall = account.data ?? null;

  return (
    <div className="space-y-4">
      <div
        className="rounded-lg flex items-center gap-3 flex-wrap"
        style={{ border: '1px solid var(--border)', padding: '10px 12px' }}
      >
        <span style={{ fontSize: '13px', fontWeight: 700 }}>생일</span>
        <span style={{ fontSize: '13px' }}>{data?.birthDate ?? '-'}</span>
        {mallUrl && (
          <a href={mallUrl} target="_blank" rel="noreferrer" className="btn-secondary ml-auto" style={compactButton}>
            복지몰 열기
          </a>
        )}
      </div>

      <div className="rounded-lg space-y-2" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
        <p style={sectionTitle}>복지몰 계정</p>
        {!mall?.loginId ? (
          <p style={mutedText}>등록된 계정이 없습니다. 인사팀이 등록하면 여기에 보입니다.</p>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-wrap" style={{ fontSize: '13px' }}>
              <span style={{ color: 'var(--text-muted)', width: '52px' }}>아이디</span>
              <span style={{ fontWeight: 600 }}>{mall.loginId}</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap" style={{ fontSize: '13px' }}>
              <span style={{ color: 'var(--text-muted)', width: '52px' }}>비밀번호</span>
              {shown ? (
                <span style={{ fontWeight: 600, letterSpacing: '0.5px' }}>{shown}</span>
              ) : mall.hasPassword ? (
                <button className="btn-secondary" style={compactButton} onClick={() => setAsking(true)}>보기</button>
              ) : (
                <span style={mutedText}>등록되지 않았습니다.</span>
              )}
            </div>
            {mall.note && <p style={mutedText}>{mall.note}</p>}
          </>
        )}

        {/* 전사 공통 사용안내. 계정이 없어도 보인다 — 회사 전체의 말이기 때문이다. */}
        {mall?.guide && (
          <p style={{ ...mutedText, whiteSpace: 'pre-wrap', paddingTop: '6px', borderTop: '1px solid var(--border)' }}>
            {mall.guide}
          </p>
        )}
      </div>

      {asking && (
        <PasswordConfirmModal
          title="복지몰 비밀번호 보기"
          message="본인 확인을 위해 비밀번호를 입력해 주세요."
          confirmLabel="보기"
          isPending={reveal.isPending}
          error={reveal.error?.message ?? null}
          onClose={() => {
            reveal.reset();
            setAsking(false);
          }}
          onConfirm={(password) =>
            reveal.mutate(password, {
              onSuccess: (result) => {
                setShown(result.password);
                setAsking(false);
              },
            })
          }
        />
      )}
    </div>
  );
}

/**
 * 수당의 다섯 갈래 — 서버의 {@code AllowanceStandard.kind} 와 <b>같은 낱말</b>이다.
 *
 * <p>사용자가 부른 순서 그대로다(2026-09-22: 「자격 근속 직책 현장 겸직」). 이름표는 화면의 말이고
 * 키는 표의 값이라 여기 한 곳에서만 잇는다 — 두 벌로 적으면 갈래가 늘 때 한쪽만 고쳐진다.
 */
const ALLOWANCE_KINDS: Array<{ key: string; label: string; hint: string | null }> = [
  { key: 'LICENSE', label: '자격', hint: '자격증이 근거인 수당' },
  { key: 'TENURE', label: '근속', hint: '근속 개월 수가 기준' },
  { key: 'DUTY', label: '직책', hint: '직책·법정 선임이 근거' },
  { key: 'SITE', label: '현장', hint: '현장 배치에 붙는다' },
  { key: 'CONCURRENT', label: '겸직', hint: '겸직·파견에 붙는다' },
  { key: 'RANK', label: '직급', hint: '직급이 기준' },
];

// ── 숙소 · 귀향여비 ──────────────────────────────────────────────────────────

const SIDO = [
  '서울특별시', '부산광역시', '대구광역시', '인천광역시', '광주광역시', '대전광역시', '울산광역시',
  '세종특별자치시', '경기도', '강원특별자치도', '충청북도', '충청남도', '전북특별자치도', '전라남도',
  '경상북도', '경상남도', '제주특별자치도',
];

/**
 * 숙소·귀향여비. **거주지는 「구」까지만** 받는다 — 판정에 필요한 정밀도가 그 정도이고,
 * 상세주소를 받는 순간 유출 시 피해가 다른 등급이 된다(DOC-47 §3.4-가).
 */
/**
 * @param mode 숙소를 보는가, 귀향여비를 보는가 (2026-09-22 에 탭이 갈렸다).
 *             <b>거주지·판정·지도는 양쪽이 같다</b> — 둘의 근거가 같은 거리 계산이기 때문이다.
 *             갈리는 것은 아래의 신청 손잡이와 이력뿐이다.
 */
function RelocationPanel({ mode }: { mode: 'lodging' | 'homecoming' }) {
  // 결재 기안 화면으로 보내던 `navigate` 가 빠졌다 — 숙소·귀향 셋 다 결재를 타지 않는다(DOC-68 §5.0).
  const { data, isPending, error } = useMyRelocation(true);
  const save = useSaveResidence();
  // 숙소만 결재를 거치지 않는다 — 여기서 바로 인사팀 요청이 선다(DOC-68 §5.0).
  const requestLodging = useRequestLodging();
  const requestHomecoming = useRequestHomecoming();
  const requestClaim = useRequestClaim();
  /** 숙소 신청서 창. 결재가 아니라 <b>요청서</b>다 — 눌러도 결재함에는 아무것도 생기지 않는다. */
  const [requesting, setRequesting] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const fill = useFillHeight();
  const [sido, setSido] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  /**
   * 미리보기로 고른 현장. 비면 내 발령 현장 판정이다. 고르면 판정 카드·지도가 그 현장 기준으로 바뀌고
   * — 본사 직원도 현장까지의 거리·여비를 찍어 볼 수 있다 — 신청은 여전히 내 발령 현장으로만 선다.
   */
  const [previewSiteId, setPreviewSiteId] = useState<string | null>(null);
  const sites = useRelocationSites(true);
  const preview = useRelocationPreview(previewSiteId);

  /**
   * 주소를 검색해 **시/도와 시·군·구만** 가져온다. 상세주소는 받은 자리에서 버린다 —
   * 화면 상태에도 담지 않으므로 저장 버튼이 보낼 것이 애초에 없다(DOC-47 §3.4-가).
   */
  const pickAddress = async () => {
    setSearchError(null);
    try {
      const picked = await searchAddress();
      if (!picked) return;
      setSido(picked.sido);
      setSigungu(picked.sigungu);
    }
    catch (ex) {
      setSearchError(ex instanceof Error ? ex.message : '주소 검색을 열지 못했습니다.');
    }
  };

  if (isPending) return <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>;
  if (error) return <Notice tone="error">자격 자료를 불러오지 못했습니다.</Notice>;

  const residence = data?.residence ?? null;
  const own = data?.eligibility ?? null;
  // 고른 현장의 답이 오기 전에는 앞의 판정을 흐리게 둔다 — 빈 칸으로 깜빡이지 않게.
  const eligibility = previewSiteId ? (preview.data ?? own) : own;

  return (
    /*
      이 탭은 <b>화면 안에서 끝난다</b> — 남는 높이를 지도가 먹되 바깥 스크롤은 만들지 않는다
      (사용자 지시 2026-08-27). 그래서 세로 비율(72vh 같은 값)이 아니라 <b>실제 남은 높이를 재서</b>
      쓴다: 머리글·탭 높이가 바뀌어도, 브라우저 주소창이 접혔다 펴져도 어긋나지 않는다.
      이력이 길면 그 목록만 안에서 스크롤되고, 페이지 자체는 그대로 있다.
    */
    <div ref={fill.ref} className="flex flex-col gap-4" style={{ height: fill.height, overflow: 'hidden' }}>
      {/*
        왼쪽에 「거주지」·「대상 판정」 두 카드를, 오른쪽에 지도를 통째로 세운다(사용자 지시 2026-08-27).
        지도를 판정 카드 안에 두면 설명 아래로 밀려 접히고, 넓은 화면에서는 오른쪽이 비어 <b>가로로만
        긴 화면</b>이 됐다. 좁은 화면(lg 미만)에서는 한 줄로 되돌아가 지도가 아래에 온다.
        두 칸일 때 줄 높이는 칸 안에 가둔다(minmax(0, 1fr)) — 기본 auto 면 카드가 길 때 줄이 칸 밖으로
        자라 왼쪽 스크롤이 듣지 않고, 아래 「숙소 신청」 이력과 겹친다. 한 칸일 때는 카드와 지도(최소
        340px)가 세로로 쌓여 다 들어갈 수 없으니 이 칸 전체가 안에서 스크롤한다.
      */}
      <div
        className="grid grid-cols-1 lg:grid-cols-2 lg:auto-rows-[minmax(0,1fr)] gap-3 items-stretch"
        style={{ flex: 1, minHeight: '280px', overflowY: 'auto' }}
      >
        {/* 한 칸일 때 스크롤을 걸면 지도에 높이를 다 내주고 0 으로 접힌다 — 그때는 바깥 칸이 스크롤한다. */}
        <div className="space-y-4 lg:overflow-y-auto">
      {/* ── 거주지 ── */}
      <div className="rounded-lg" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
        <p style={sectionTitle}>거주지</p>
        <p style={mutedText}>
          시·군·구까지만 등록합니다. 상세주소는 받지 않으며, 좌표는 구 중심 기준이라 약 ±5km 오차가 있습니다.
        </p>
        <div className="flex items-end gap-2 flex-wrap" style={{ marginTop: '8px' }}>
          {/*
            검색으로 고른 주소는 행정 자료 그대로라 지오코딩이 실패하지 않는다. 손으로 치면
            「강서」·「경기 평택」 처럼 줄여 적기 쉽고, 그 실패는 화면에 「자료 부족」 으로만 나타난다.
          */}
          <button className="btn-secondary" style={compactButton} onClick={pickAddress}>
            주소 검색
          </button>
          <select
            className="input-field"
            aria-label="시/도"
            style={{ fontSize: '13px', padding: '5px 8px', maxWidth: '180px' }}
            value={sido || residence?.sido || ''}
            onChange={(e) => setSido(e.target.value)}
          >
            <option value="">시/도 선택</option>
            {SIDO.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          <input
            className="input-field"
            aria-label="시/군/구"
            style={{ fontSize: '13px', padding: '5px 8px', maxWidth: '180px' }}
            placeholder="예: 강서구"
            value={sigungu || residence?.sigungu || ''}
            onChange={(e) => setSigungu(e.target.value)}
          />
          <button
            className="btn-secondary"
            style={compactButton}
            disabled={save.isPending}
            onClick={() => save.mutate({
              sido: sido || residence?.sido || '',
              sigungu: sigungu || residence?.sigungu || '',
            })}
          >
            {save.isPending ? '저장 중…' : '저장'}
          </button>
          {residence && <span style={mutedText}>{residence.label}</span>}
        </div>
        {searchError && (
          <p style={{ ...mutedText, color: 'var(--danger)', marginTop: '4px' }}>{searchError}</p>
        )}
        {residence && residence.geocodeStatus !== 'OK' && (
          <p style={{ ...mutedText, color: 'var(--danger)', marginTop: '4px' }}>
            좌표를 확보하지 못해 대상 판정이 서지 않습니다. 인사팀에 문의해 주세요.
          </p>
        )}
      </div>

      {/* ── 판정 ── */}
      {eligibility && (
        <EligibilityCard
          mode={mode}
          eligibility={eligibility}
          sites={sites.data ?? []}
          ownSiteName={own?.siteName ?? null}
          previewSiteId={previewSiteId}
          onPreviewSite={setPreviewSiteId}
          previewFetching={!!previewSiteId && preview.isFetching}
          previewError={previewSiteId ? (preview.error?.message ?? null) : null}
          lodging={data?.lodgingApplications ?? []}
          homecoming={data?.homecomingApplications ?? []}
          onRequestLodging={() => {
            requestLodging.reset();
            setRequesting(true);
          }}
          onRequestHomecoming={() => requestHomecoming.mutate({})}
          onRequestClaim={() => {
            requestClaim.reset();
            setClaiming(true);
          }}
          requesting={requestLodging.isPending}
          requestingHomecoming={requestHomecoming.isPending}
          requestError={requestLodging.error?.message ?? requestHomecoming.error?.message ?? null}
        />
      )}
        </div>

        {/* ── 지도(오른쪽 한 칸을 통째로) ── */}
        {/* 인사팀 승인 화면과 <b>같은 지도</b>다(components/RelocationRouteMap · DOC-68 §8-B.2). */}
        <RelocationRouteMap
          residenceLat={eligibility?.residenceLat ?? null}
          residenceLng={eligibility?.residenceLng ?? null}
          siteLat={eligibility?.siteLat ?? null}
          siteLng={eligibility?.siteLng ?? null}
          siteName={eligibility?.siteName ?? null}
          residenceLabel={eligibility?.residenceLabel ?? null}
          missingHint={
            // 발령 없는 본사 직원은 판정이 서지 않아 좌표가 비어 온다 — 거주지가 아니라 현장이 빈 것이다.
            eligibility != null && eligibility.siteName == null
              ? '판정 카드에서 현장을 고르면 위치가 표시됩니다.'
              : eligibility?.residenceLat == null
              ? '왼쪽에서 거주지를 등록하면 위치가 표시됩니다.'
              : '배치 현장에 주소가 없습니다 — 현장 관리에서 주소를 넣어 주세요.'
          }
        />
      </div>

      {/*
        신청서 창. **결재가 아니다** — [요청 보내기]를 누르면 그대로 인사팀 목록에 선다.
        창을 띄우는 이유는 하나다: 입주 예정일·인원·희망 사항을 받아야 인사팀이 방을 고를 수 있다.
      */}
      {/* 신청서는 언제나 <b>내 발령 현장</b> 판정으로 연다 — 미리보기로 고른 현장이 아니다. */}
      {claiming && own && (
        <ClaimRequestModal
          eligibility={own}
          approved={data?.homecomingApplications.find((row) => row.status === 'APPROVED') ?? null}
          claims={data?.claims ?? []}
          pending={requestClaim.isPending}
          error={requestClaim.error?.message ?? null}
          onClose={() => setClaiming(false)}
          onSubmit={(body) => requestClaim.mutate(body, { onSuccess: () => setClaiming(false) })}
        />
      )}

      {requesting && own && (
        <LodgingRequestModal
          eligibility={own}
          pending={requestLodging.isPending}
          error={requestLodging.error?.message ?? null}
          onClose={() => setRequesting(false)}
          onSubmit={(body) => requestLodging.mutate(body, { onSuccess: () => setRequesting(false) })}
        />
      )}

      {/*
        ── 이력 ── 있을 때만 자리를 차지하고, 길어지면 <b>이 안에서만</b> 스크롤한다.
        페이지째 늘어나면 지도가 화면 밖으로 밀려 「꽉 찬 한 화면」 이 깨진다.
      */}
      {((mode === 'lodging' && (data?.lodgingApplications.length ?? 0) > 0)
        || (mode === 'homecoming' && (data?.claims.length ?? 0) > 0)) && (
        <div className="space-y-4 overflow-y-auto" style={{ flexShrink: 0, maxHeight: '32%' }}>
      {mode === 'lodging' && (data?.lodgingApplications.length ?? 0) > 0 && (
        <div>
          <p style={sectionTitle}>숙소 신청</p>
          {data!.lodgingApplications.map((row) => (
            <div key={row.id} className="flex items-center gap-3" style={rowStyle}>
              <span style={{ fontWeight: 600 }}>{row.siteName}</span>
              <span style={mutedText}>{row.checkInDate ?? ''}</span>
              {row.roomLabel && <span className="badge badge-blue">{row.roomLabel}</span>}
              <span className="ml-auto badge badge-gray">{row.status}</span>
            </div>
          ))}
        </div>
      )}

      {mode === 'homecoming' && (data?.claims.length ?? 0) > 0 && (
        <div>
          <p style={sectionTitle}>귀향여비 정산</p>
          {data!.claims.map((claim) => (
            <div key={claim.id} className="flex items-center gap-3" style={rowStyle}>
              <span style={{ fontWeight: 600, width: '72px' }}>{claim.claimMonth}</span>
              {/*
                셋을 나란히 적는다 — 실비·한도·지급액. 지급액만 보이면 "왜 이만큼만" 을 물을 수 없다.
              */}
              <span style={mutedText}>실비 {claim.claimedAmount.toLocaleString('ko-KR')}원</span>
              <span style={mutedText}>한도 {claim.capAmount.toLocaleString('ko-KR')}원</span>
              <span className="ml-auto" style={{ fontWeight: 700 }}>
                {claim.payableAmount.toLocaleString('ko-KR')}원
              </span>
              <span className="badge badge-gray">{claim.status}</span>
            </div>
          ))}
        </div>
      )}
        </div>
      )}
    </div>
  );
}

function EligibilityCard({
  mode,
  eligibility,
  sites,
  ownSiteName,
  previewSiteId,
  onPreviewSite,
  previewFetching,
  previewError,
  lodging,
  homecoming,
  onRequestLodging,
  onRequestHomecoming,
  onRequestClaim,
  requesting,
  requestingHomecoming,
  requestError,
}: {
  /** 어느 탭이 부르는가 — 손잡이가 그만큼만 선다(2026-09-22 에 탭이 갈렸다). */
  mode: 'lodging' | 'homecoming';
  eligibility: NonNullable<MyRelocation['eligibility']>;
  /** 미리보기로 고를 수 있는 현장(주소 있는 것만). */
  sites: Array<{ id: string; name: string }>;
  ownSiteName: string | null;
  /** 고른 현장. 비면 내 발령 현장 판정이다. */
  previewSiteId: string | null;
  onPreviewSite: (siteId: string | null) => void;
  previewFetching: boolean;
  previewError: string | null;
  /** 내 숙소 신청 이력. <b>버튼을 낼지 상태를 낼지</b>가 여기서 갈린다. */
  lodging: MyRelocation['lodgingApplications'];
  homecoming: MyRelocation['homecomingApplications'];
  onRequestLodging: () => void;
  onRequestHomecoming: () => void;
  onRequestClaim: () => void;
  requesting: boolean;
  requestingHomecoming: boolean;
  /** 서버가 거절한 사유(대상 아님·이미 신청 중). <b>그대로 보여 준다</b> — 무엇을 고쳐야 하는지가 그 문장에 있다. */
  requestError: string | null;
}) {
  const eligible = eligibility.tier !== 'NONE';
  /*
    ★ **진행 중인 신청이 있으면 버튼 자리에 상태가 온다** (사용자 지적 2026-08-27:
      「신청을 눌렀으면, 요청중 … 하고 완료 됐으면 이 관련 칸을 이제 가리고 … 그런게 전혀 안나오잖아」).

    누른 뒤에도 같은 버튼이 그대로 있으면 사람은 **눌리지 않은 줄 안다.** 그래서 한 번 더 누르고,
    두 번째는 「이미 처리 중입니다」 라는 빨간 글씨를 받는다 — 제대로 된 일을 하고 오류를 본다.
    서버가 한 건으로 막는 것(`findOpenLodging`)과 <b>같은 사실</b>을 화면도 알고 있어야 한다.
  */
  const openLodging = lodging.find((row) => row.status === 'REQUESTED' || row.status === 'APPROVED') ?? null;
  // 열린 신청이 없을 때만 본다 — 반려된 뒤 다시 신청해 진행 중이면 그 옛 반려는 지난 일이다.
  const lastRejected = openLodging ? null : (lodging.find((row) => row.status === 'REJECTED') ?? null);
  /** 귀향은 방이 없어 상태가 둘뿐이다 — 요청했나 · 한도가 섬니다. */
  const openHomecoming = homecoming.find((row) => row.status === 'REQUESTED' || row.status === 'APPROVED') ?? null;

  return (
    <div className="rounded-lg" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span style={{ fontSize: '13px', fontWeight: 700 }}>대상 판정</span>
        <span className={eligible ? 'badge badge-green' : 'badge badge-gray'}>{eligibility.tierLabel}</span>
        {eligibility.borderline && (
          <span className="badge badge-yellow" title="거주지 좌표가 구 중심이라 오차가 있습니다.">
            경계 구간 · 담당자 확인
          </span>
        )}
        <select
          className="input-field ml-auto"
          aria-label="현장"
          style={{ ...fieldStyle, maxWidth: '220px' }}
          value={previewSiteId ?? ''}
          onChange={(e) => onPreviewSite(e.target.value || null)}
        >
          <option value="">{ownSiteName ? `내 배치 현장 · ${ownSiteName}` : '현장 선택'}</option>
          {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
        </select>
      </div>
      {previewError && <p style={{ ...mutedText, color: 'var(--danger)', marginTop: '4px' }}>{previewError}</p>}

      <div style={{ opacity: previewFetching ? 0.5 : 1 }}>

      {/*
        숫자를 다 적는다 — "대상입니다/아닙니다" 로 끝내면 이의를 제기할 근거가 없다.
        거리에는 **어느 자로 잰 값인지**를 붙인다. 사람이 지도에서 재 보고 우리 값과 다르면
        가장 먼저 의심하는 것이 그 차이이기 때문이다(직선 45km = 도로 58km 는 흔하다).
      */}
      <p style={{ ...mutedText, marginTop: '4px' }}>
        {eligibility.residenceLabel ?? '거주지 미등록'} → {eligibility.siteName ?? '배치 현장 없음'}
        {eligibility.distanceKm != null && ` · ${distanceLabel(eligibility.distanceSource)} ${eligibility.distanceKm}km`}
        {eligibility.travelMinutes != null && ` · ${eligibility.travelMinutes}분`}
      </p>
      {eligibility.reason && <p style={{ ...mutedText, marginTop: '2px' }}>{eligibility.reason}</p>}
      {/*
        어떻게 낸 값인지 적는다 — 엔진에 따라 「무엇을 반영한 값인가」 가 다르고, 사람이 이의를
        제기하려면 그 차이를 알아야 한다. 카카오는 실시간 교통이 들어간 3경로 평균이고, 물러선
        OSRM 은 교통을 안 본다.
      */}
      {eligibility.distanceSource === 'KAKAO' ? (
        <p style={{ ...mutedText, marginTop: '2px' }}>
          거리·시간 모두 카카오 길찾기 <b>3개 경로(추천·최소시간·최단거리)의 평균</b>입니다.
          {eligibility.straightLineKm != null && ` 직선거리로는 ${eligibility.straightLineKm}km 입니다.`}
        </p>
      ) : eligibility.distanceSource === 'STRAIGHT' ? (
        <p style={{ ...mutedText, marginTop: '2px', color: 'var(--danger)' }}>
          경로를 받지 못해 <b>직선거리</b>로 냈습니다 — 실제 도로거리는 이보다 깁니다. 대상 여부가
          바뀔 수 있어 담당자 확인 대상으로 넘어갑니다.
        </p>
      ) : eligibility.distanceKm != null ? (
        <p style={{ ...mutedText, marginTop: '2px' }}>
          도로거리 기준이며 실시간 교통은 반영되지 않았습니다({eligibility.distanceSource}).
          {eligibility.straightLineKm != null && ` 직선거리로는 ${eligibility.straightLineKm}km 입니다.`}
        </p>
      ) : null}

      {/*
        특별지역은 한도가 0 으로 온다 — 구간표를 태우지 않기 때문이다(V166). 그 0 을 그대로
        보여 주면 「대상이 아니다」 로 읽히므로, 금액 자리에 <b>왜 비었는지</b>를 적는다.
      */}
      {eligibility.specialArea ? (
        <p style={{ fontSize: '13px', marginTop: '6px' }}>
          <b>특별지역(제주·도서)</b> 현장입니다 — 귀향여비는 구간표로 계산하지 않고{' '}
          <b>별도로 지급</b>합니다.
          {/* <span style={mutedText}> 금액은 인사팀이 신청 건마다 정합니다.</span> */}
        </p>
      ) : eligibility.monthlyCap != null && eligibility.monthlyCap > 0 && (
        <p style={{ fontSize: '13px', marginTop: '6px' }}>
          귀향여비 월 한도 <b>{eligibility.monthlyCap.toLocaleString('ko-KR')}원</b>
          {eligibility.roundTripAmount != null && eligibility.monthlyRoundTrips != null && (
            <span style={mutedText}>
              {' '}({eligibility.roundTripAmount.toLocaleString('ko-KR')}원 × 월 {eligibility.monthlyRoundTrips}회)
            </span>
          )}
        </p>
      )}
      </div>

      {/* 신청 손잡이는 내 발령 현장 판정에만 붙는다 — 고른 현장은 보기만 한다(사용자 결정 2026-10-20). */}
      {eligible && !previewSiteId && (
        <div className="space-y-1" style={{ marginTop: '8px' }}>
          <div className="flex items-center gap-2 flex-wrap">
            {/*
              ★ **숙소 신청은 결재를 타지 않는다** (사용자 지시 2026-08-27 · DOC-68 §5.0):
                「숙소 신청을 하는 경우는 그냥 기안으로 가지말고 바로 인사팀 요청으로 바로 보내자」

              회사 숙소 배정은 <b>자원 배분</b>이지 지출 결정이 아니다. 방이 남았는지는 인사팀만
              알고 자격은 거리로 이미 갈렸으니, 결재선에 선 사람이 신청자보다 아는 것이 적다.
              누르면 그 자리에서 요청이 서고, 아래 「숙소 신청」 목록에 바로 나타난다.
            */}
            {mode === 'lodging' && (openLodging ? (
              <LodgingState row={openLodging} />
            ) : (
              <button
                className="btn-secondary"
                style={compactButton}
                disabled={requesting}
                /*
                  창을 하나 띄운다 — 입주 예정일·인원·희망 사항을 받기 위해서다(사용자 지적
                  2026-08-27: 「희망은 어디서 쓰는건데?」). 그전에는 빈 몸으로 나가서 인사팀 승인
                  창의 그 칸들이 <b>영영 비어 있었다.</b> 창이 뜨는 것과 결재를 타는 것은 다르다.
                */
                onClick={onRequestLodging}
              >
                {requesting ? '요청 중…' : lastRejected ? '숙소 다시 신청' : '숙소 신청'}
              </button>
            ))}
            {/*
              ★ **귀향여비도 결재를 타지 않는다** (사용자 지시 2026-08-27: 「귀향여비도 결재 말고
                바로 처리하게 하자. 돈이거는 결재 탄다기보다 어챌든 인사팀이 처리해줄 문제라고
                생각해」). 「돈이 나가면 결재」 를 기본값으로 두었던 것을 사람이 뒤집었다 —
                금액은 구간표가 정하고 영수증은 인사팀이 본다. 결재선이 더할 판단이 없다.
            */}
            {mode === 'homecoming' && (openHomecoming ? (
              <span className="badge badge-blue">
                귀향여비 {openHomecoming.status === 'REQUESTED' ? '요청함 · 처리 대기' : '대상 승인됨'}
                {openHomecoming.monthlyCap != null && ` · 월 한도 ${openHomecoming.monthlyCap.toLocaleString('ko-KR')}원`}
              </span>
            ) : (
              <button
                className="btn-secondary"
                style={compactButton}
                disabled={requestingHomecoming}
                onClick={onRequestHomecoming}
              >
                {requestingHomecoming ? '요청 중…' : '귀향여비 대상 신청'}
              </button>
            ))}
            {/* 정산은 <b>승인된 대상</b>이 있어야 한다 — 한도 없이 청구하면 지급액이 0으로 굳는다. */}
            {mode === 'homecoming' && (
              <button
                className="btn-secondary"
                style={compactButton}
                disabled={openHomecoming?.status !== 'APPROVED'}
                title={openHomecoming?.status === 'APPROVED' ? undefined : '먼저 「귀향여비 대상 신청」 이 승인되어야 합니다'}
                onClick={onRequestClaim}
              >
                귀향여비 정산
              </button>
            )}
          </div>

          {/*
            셋 다 결재를 타지 않는다는 것을 <b>한 줄로</b> 적는다. 없으면 누른 사람이 결재함을 열어
            보고 「안 올라갔다」 고 여긴다 — 실제로 겪은 오해다(DOC-68 §5.8).
          */}
          <p style={mutedText}>
            {mode === 'lodging' ? '숙소 신청은' : '귀향여비는'} <b>결재를 거치지 않고</b> 인사팀으로 바로 갑니다 — 결재함에는 나타나지 않습니다.
          </p>
          {/*
            반려는 <b>끝난 일</b>이라 진행 상태 칸에 넣지 않는다. 그런데 사유가 없으면 다시 신청할
            때 같은 이유로 또 반려된다 — 그래서 버튼 옆이 아니라 아래에 사유를 그대로 남긴다.
          */}
          {mode === 'lodging' && lastRejected && (
            <p style={{ ...mutedText, color: 'var(--danger)' }}>
              지난 신청이 반려되었습니다{lastRejected.rejectReason ? ` — ${lastRejected.rejectReason}` : '.'}
            </p>
          )}
          {requestError && <p style={{ ...mutedText, color: 'var(--danger)' }}>{requestError}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * 진행 중인 숙소 신청의 <b>지금 상태</b>. 신청 버튼이 있던 자리에 대신 선다.
 *
 * ## 「승인」이 끝이 아니다
 * 숙소는 승인과 <b>배정</b>이 다르다(V78). 방이 정해지기 전의 승인은 「대상이 맞다」 까지이고,
 * 그 사이의 사람은 <b>어디로 들어가야 할지 모른다</b>. 상태를 하나로 뭉쳐 「승인됨」 이라고만
 * 적으면 그 사람은 짐을 싸도 되는 줄 안다.
 *
 * ## 다음에 무슨 일이 일어나는지 적는다
 * 「요청함」 만으로는 기다리면 되는지 무언가 더 해야 하는지 알 수 없다. 상태마다 <b>다음 걸음</b>이
 * 한 줄 붙는다 — 인사팀 화면의 `fix` 와 같은 결이다(DOC-68 §5.9).
 */
function LodgingState({ row }: { row: MyRelocation['lodgingApplications'][number] }) {
  const assigned = row.status === 'APPROVED' && !!row.roomLabel;
  const waiting = row.status === 'REQUESTED';

  return (
    <span className="flex items-center gap-2 flex-wrap">
      <span className={assigned ? 'badge badge-green' : waiting ? 'badge badge-yellow' : 'badge badge-blue'}>
        {waiting ? '요청함 · 인사팀 처리 대기' : assigned ? `${row.roomLabel} 배정` : '승인됨 · 방 배정 대기'}
      </span>
      <span style={mutedText}>
        {row.siteName}
        {row.checkInDate && ` · 입주 ${row.checkInDate}`}
      </span>
      <span style={mutedText}>
        {waiting
          ? '인사팀이 방을 정하면 여기에 방 이름이 표시됩니다. 따로 하실 일은 없습니다.'
          : assigned
            ? '배정이 끝났습니다. 전입신고·제출용 확인서가 필요하면 인사팀에 요청하세요.'
            : '대상으로 승인되었고 방을 고르는 중입니다.'}
      </span>
    </span>
  );
}

// ── 거들기 ───────────────────────────────────────────────────────────────────


/**
 * 화면에 <b>남은 높이</b>를 재서 돌려준다 — 그만큼만 차지하면 바깥 스크롤이 생기지 않는다.
 *
 * <p>`calc(100vh - 260px)` 같은 식을 쓰지 않는 이유: 머리글·탭의 높이는 글자 크기·줄바꿈·배너
 * 유무로 바뀐다. 한 번 어긋나면 <b>스크롤이 몇 픽셀씩 생기는</b> 형태로 나타나 원인을 찾기 나쁘다.
 * 요소의 실제 위치를 재면 그 계산이 필요 없다.
 *
 * <p>`100dvh` 를 쓰는 이유는 모바일이다 — `vh` 는 주소창이 접히기 전 높이라, 그대로 쓰면 화면
 * 아래가 잘린다.
 */
const MIN_FILL_HEIGHT = 360;

function useFillHeight() {
  // 콜백 ref 다. 이 패널은 자료를 기다리는 동안 <b>다른 것을 그리다가</b> 나중에 이 칸이 붙는다 —
  // useRef 로 받으면 붙는 순간을 놓쳐 높이가 영영 정해지지 않는다.
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  /**
   * 조상의 아래 여백만큼 빼 둘 값. <b>기억해 두는 것이 핵심이다</b> — 매번 다시 재기만 하면
   * 「줄였다가 → 남으니 다시 늘렸다가」 를 반복하며 화면이 떤다.
   */
  const slack = useRef(0);

  useEffect(() => {
    if (!box) return;
    let frame = 0;

    const measure = () => {
      const top = box.getBoundingClientRect().top;
      // 주소창이 접히는 모바일에서는 visualViewport 가 진짜 보이는 높이다.
      const viewport = window.visualViewport?.height ?? window.innerHeight;
      const next = Math.max(MIN_FILL_HEIGHT, Math.floor(viewport - top - 8 - slack.current));
      setHeight(next);
      cancelAnimationFrame(frame);

      /*
        2차 보정 — <b>조상의 아래 여백</b>은 위치 측정으로 알 수 없다. 페이지 컨테이너에
        `padding-bottom` 이 있으면 우리가 딱 맞춰도 그만큼 넘쳐 스크롤이 생긴다. 한 번 그려 본 뒤
        실제로 넘친 양을 재서 빼 두면, 여백이 얼마든 <b>두 번째 그림에서 정확히</b> 맞는다.
        이미 최소 높이에 걸렸으면 넘침은 피할 수 없다 — 그때 줄이면 다음 측정이 다시 최소로
        올려 두 값 사이를 매 프레임 오간다(노트북에서 화면이 떨던 원인).
      */
      if (next <= MIN_FILL_HEIGHT) return;
      frame = requestAnimationFrame(() => {
        const root = document.documentElement;
        const overflow = root.scrollHeight - root.clientHeight;
        if (overflow <= 0) return;
        slack.current += overflow;
        setHeight(Math.max(MIN_FILL_HEIGHT, next - overflow));
      });
    };

    measure();
    window.addEventListener('resize', measure);
    /*
      칸의 <b>폭</b>이 바뀔 때만 다시 잰다. 높이는 이 훅이 정하는 값이라, 그 변화에 반응하면
      제가 바꾼 높이를 보고 또 바꾸는 고리가 된다. 내용이 늘어도 칸은 고정 높이·overflow hidden 이다.
    */
    let lastWidth = box.getBoundingClientRect().width;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? lastWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      measure();
    });
    observer.observe(box);

    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [box]);

  return { ref: setBox, height };
}


/**
 * 거리 앞에 붙는 낱말. <b>단위만 적고 자를 안 적으면</b> 사람은 자기가 아는 자(지도에서 본 도로거리)로
 * 읽는다 — 우리가 직선으로 냈을 때 그 오해가 곧 「시스템이 틀렸다」 가 된다.
 */
function distanceLabel(source: string | null): string {
  return source === 'STRAIGHT' ? '직선거리' : '도로거리';
}

/** 기준이 비었다는 것과 대상이 아니라는 것은 다른 말이다. 화면이 그 둘을 구분해 말한다. */
function StandardMissing({ what }: { what: string }) {
  return (
    <Notice tone="info">
      {what}이 아직 등록되지 않았습니다. 인사팀이 사내 지침을 등록하면 여기에 대상 여부가 표시됩니다.
    </Notice>
  );
}

const compactButton = { fontSize: '12px', padding: '5px 12px' } as const;
const fieldStyle = { fontSize: '13px', padding: '5px 8px' } as const;
const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
const sectionTitle = { fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' } as const;
const rowStyle = { padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' } as const;
